"use client";
import { useState, useEffect, useRef, useCallback } from "react";
import { usePathname } from "next/navigation";
import type { PanelId, SectionMeta } from "@/entities/section";
import type { Slide } from "@/entities/slide";
import { GroupTile } from "./GroupTile";
import { SectionTile } from "./SectionTile";
import { parseSection } from "../lib/parseSection";
import { PANELS, ANIM_DURATION_MS, TILE_TR } from "../lib/constants";
import type { TileRect, SwitchInfo } from "../model/types";
import { useTouchDevice } from "@/shared/lib/useTouchDevice";
import { useMediaQuery } from "@/shared/lib/useMediaQuery";

interface TriadRingProps {
  children?: React.ReactNode;
  slides: Slide[];
  sections: SectionMeta[];
}

export function TriadRing({ children, slides, sections }: TriadRingProps) {
  const GROUP_SLIDES = slides;
  const SECTION_TO_SLIDE = Object.fromEntries(
    slides
      .map((s, i) => [s.sectionId, i] as const)
      .filter(([sid]) => !!sid),
  ) as Record<PanelId, number>;
  const META = Object.fromEntries(
    sections.map((s) => [s.id, s] as const),
  ) as Record<PanelId, SectionMeta>;
  const pathname = usePathname();
  const isIgnoredRoute = pathname.startsWith("/specialists");
  const urlSection = parseSection(pathname);

  // activeSection — derived from URL. For /specialists/* keep the last real
  // section so the ring doesn't fall back to home while a specialist modal
  // is open.
  const lastSectionRef = useRef<PanelId | null>(urlSection);
  useEffect(() => {
    if (urlSection) lastSectionRef.current = urlSection;
  }, [urlSection]);
  const activeSection: PanelId | null = isIgnoredRoute
    ? (lastSectionRef.current ?? "help")
    : urlSection;

  // Derived mode — no useState, computed each render from URL.
  const mode: "home" | "section" = activeSection ? "section" : "home";
  const isHome = mode === "home";
  const isSection = mode === "section";

  // isAnimating — true while tiles are in motion.
  // Triggered on mode change, reset by onTransitionEnd on the group tile
  // (with a safety timeout fallback).
  const [isAnimating, setIsAnimating] = useState(false);
  // contentMounted: stays true for 450ms after isSection→false so opacity transition plays before display:none
  const [contentMounted, setContentMounted] = useState(false);
  const lastModeRef = useRef<"home" | "section">(mode);
  useEffect(() => {
    if (lastModeRef.current !== mode) {
      setIsAnimating(true);
      lastModeRef.current = mode;
    }
  }, [mode]);

  // Safety fallback: if onTransitionEnd never fires (e.g. widths equal,
  // tab backgrounded, Safari edge case), force-reset after ANIM_DURATION_MS + 50 ms.
  useEffect(() => {
    if (!isAnimating) return;
    const t = setTimeout(() => setIsAnimating(false), ANIM_DURATION_MS + 50);
    return () => clearTimeout(t);
  }, [isAnimating]);

  // Delayed content unmount — keeps display:block during the 0.4s opacity fade-out
  useEffect(() => {
    if (isSection) {
      setContentMounted(true);
    } else {
      const t = setTimeout(() => setContentMounted(false), 450);
      return () => clearTimeout(t);
    }
  }, [isSection]);

  const isTouch = useTouchDevice();

  const [activeTouchId, setActiveTouchId] = useState<PanelId | null>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const groupTileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (ringRef.current && !ringRef.current.contains(e.target as Node))
        setActiveTouchId(null);
    };
    document.addEventListener("click", fn);
    return () => document.removeEventListener("click", fn);
  }, []);

  const [hoveredTile, setHoveredTile] = useState<string | null>(null);
  const [isPageScrolled, setIsPageScrolled] = useState(false);
  // groupIsNarrow: viewport ≤ 769px → CSS overrides --group-w to var(--bar-h)
  // via @container in globals.css. TS flag kept (matchMedia, SSR-safe via
  // useSyncExternalStore) for mobile rect-layout branch in getSectionRect.
  // Spec §6.4: media query allowed; window.innerWidth forbidden.
  const groupIsNarrow = useMediaQuery("(max-width: 769px)");
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isScrolling = useRef(false);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onScroll = () => {
      setIsPageScrolled(window.scrollY > 64);
      isScrolling.current = true;
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => {
        isScrolling.current = false;
        scrollTimerRef.current = null;
      }, 200);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    };
  }, []);

  const hoverEnter = useCallback((id: string) => {
    if (isScrolling.current) {
      return;
    }
    // No hover effects on pure-touch devices
    if (isTouch) {
      return;
    }
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredTile(id);
  }, [isTouch]);

  const hoverLeave = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      // Don't clear hover state while user is scrolling — keeps active section highlighted
      if (isScrolling.current) {
        hoverTimeoutRef.current = null;
        return;
      }
      setHoveredTile(null);
      hoverTimeoutRef.current = null;
    }, 80);
  }, []);

  const [groupSlideIndex, setGroupSlideIndex] = useState(0);
  const [sliderPinnedSection, setSliderPinnedSection] =
    useState<PanelId | null>(null);
  const [pinnedSide, setPinnedSide] = useState<'left' | 'right'>('right');
  const [switchInfo, setSwitchInfo] = useState<SwitchInfo>(null);
  const isProtectedSlide =
    groupSlideIndex !== 0 && !GROUP_SLIDES[groupSlideIndex]?.sectionId;
  const prevPathRef = useRef(pathname);
  const switchTimers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const prev = prevPathRef.current;
    if (prev === pathname) return;
    prevPathRef.current = pathname;

    const prevSection: PanelId | null =
      parseSection(prev) ??
      (prev.startsWith("/specialists") ? "help" : null);
    const nextSection: PanelId | null =
      urlSection ?? (isIgnoredRoute ? "help" : null);

    // home → section
    if (!prevSection && nextSection) {
      setSliderPinnedSection(null);
      if (SECTION_TO_SLIDE[nextSection] !== undefined) {
        setGroupSlideIndex(SECTION_TO_SLIDE[nextSection]);
      }
      setHoveredTile(null);
    }
    // section → home
    else if (prevSection && !nextSection) {
      const returnSlide = SECTION_TO_SLIDE[prevSection] ?? 0;
      setGroupSlideIndex(returnSlide);
      setSliderPinnedSection(prevSection);
      setPinnedSide("right");
    }
    // section → section' — directional slide via translate3d overlay
    // (preserves the original UX: tile slides in from one side, neighbour from other).
    else if (prevSection && nextSection && prevSection !== nextSection) {
      setHoveredTile(null);
      if (SECTION_TO_SLIDE[nextSection] !== undefined) {
        setGroupSlideIndex(SECTION_TO_SLIDE[nextSection]);
      }
      const fwd =
        (PANELS.indexOf(nextSection) - PANELS.indexOf(prevSection) + 3) % 3;
      const dir: 1 | -1 = fwd === 1 ? 1 : -1;
      switchTimers.current.forEach(clearTimeout);
      switchTimers.current = [];
      setSwitchInfo({ from: prevSection, to: nextSection, dir, phase: "init" });
      switchTimers.current.push(
        setTimeout(
          () => setSwitchInfo((s) => (s ? { ...s, phase: "go" } : null)),
          20,
        ),
        setTimeout(() => {
          setSwitchInfo(null);
        }, ANIM_DURATION_MS),
      );
    }
  }, [pathname, urlSection, isIgnoredRoute]);

  useEffect(
    () => () => {
      switchTimers.current.forEach(clearTimeout);
    },
    [],
  );

  const onPillClick = (e: React.MouseEvent, p: PanelId) => {
    e.stopPropagation();
    // "Развернуть": expand tile locally, stay on home page
    if (isHome && SECTION_TO_SLIDE[p] !== undefined) {
      setGroupSlideIndex(SECTION_TO_SLIDE[p]);
      setPinnedSide('right');
      setSliderPinnedSection(p);
    }
    // Navigation in section mode handled by <Link>
  };
  const isExpandedFor = (p: PanelId) =>
    sliderPinnedSection === p && isHome;
  // First-tap focus guard for touch devices in home mode only.
  // Compact bars (section mode) allow single-tap navigation directly.
  const onLinkClick = (p: PanelId) => (e: React.MouseEvent) => {
    if (isTouch && !barsCollapsed && activeTouchId !== p && !isExpandedFor(p)) {
      e.preventDefault();
      setActiveTouchId(p);
    }
  };

  // Geometry flags — collapsed from former 5-phase FSM to a single derived bool.
  const groupRearranged = isSection;
  const sectionsRearranged = isSection;
  const barsCollapsed = isSection;
  const nextInRing = (id: PanelId) => PANELS[(PANELS.indexOf(id) + 1) % 3];

  const flexes = PANELS.map((pp) => {
    if (!isProtectedSlide && sliderPinnedSection === pp) return 3;
    return 1;
  });
  const totalFlex = flexes[0] + flexes[1] + flexes[2];

  const groupRect: TileRect = groupRearranged
    ? {
        top: "var(--gap)",
        left: "var(--gap)",
        width: "var(--group-w)",
        height: barsCollapsed ? "var(--bar-h)" : "var(--half-h)",
      }
    : {
        top: "var(--gap)",
        left: "var(--gap)",
        width: "var(--avail-w)",
        height: "var(--half-h)",
      };

  function getSectionRect(p: PanelId): TileRect {
    if (!sectionsRearranged) {
      const idx = PANELS.indexOf(p);

      // Mobile alt-layout for section tiles (≤769px viewport).
      // M6.B-5 plan-B: TS-logic kept here (CSS-only solution required ~15
      // pinned-side × id × expanded combinations — too invasive for atomic
      // refactor). `groupIsNarrow` source is `matchMedia` via useSyncExternalStore
      // (see line ~88), spec §6.4-compliant (media query, not window.innerWidth).
      if (groupIsNarrow) {
        // Если есть развернутая секция
        if (sliderPinnedSection) {
          const isPinnedRight = pinnedSide === 'right';

          if (p === sliderPinnedSection) {
            // Распределение: [gap][expanded: 2/3*(avail-gap)][gap][collapsed: 1/3*(avail-gap)]
            // Горизонтальный gap = var(--gap) с обеих сторон — как вертикальный между строками
            const leftPos = isPinnedRight
              ? "calc(var(--gap) * 2 + (var(--avail-w) - var(--gap)) / 3)"
              : "var(--gap)";

            return {
              top: "calc(var(--gap) * 2 + var(--half-h))",
              left: leftPos,
              width: "calc((var(--avail-w) - var(--gap)) * 2 / 3)",
              height: "var(--half-h)",
            };
          } else {
            const otherPanels = PANELS.filter(panel => panel !== sliderPinnedSection);
            const isTop = p === otherPanels[0];

            const leftPos = isPinnedRight
              ? "var(--gap)"
              : "calc(var(--gap) * 2 + (var(--avail-w) - var(--gap)) * 2 / 3)";

            return {
              top: isTop
                ? "calc(var(--gap) * 2 + var(--half-h))"
                : "calc(var(--gap) * 2 + var(--half-h) + (var(--half-h) - var(--gap)) / 2 + var(--gap))",
              left: leftPos,
              width: "calc((var(--avail-w) - var(--gap)) / 3)",
              height: "calc((var(--half-h) - var(--gap)) / 2)",
            };
          }
        } else {
          // Если ничего не развернуто — на мобилке три строки, одна под другой
          const rowH = "calc((var(--half-h) - var(--gap) * 2) / 3)";
          const topPos =
            idx === 0
              ? "calc(var(--gap) * 2 + var(--half-h))"
              : idx === 1
                ? "calc(var(--gap) * 2 + var(--half-h) + (var(--half-h) - var(--gap) * 2) / 3 + var(--gap))"
                : "calc(var(--gap) * 2 + var(--half-h) + 2 * ((var(--half-h) - var(--gap) * 2) / 3 + var(--gap)))";
          return {
            top: topPos,
            left: "var(--gap)",
            width: "var(--avail-w)",
            height: rowH,
          };
        }
      }

      // Десктопная логика (как было)
      return {
        top: "calc(var(--gap) * 2 + var(--half-h))",
        left: `var(--left-${idx})`,
        width: `var(--w-${idx})`,
        height: "var(--half-h)",
      };
    }
    if (p === activeSection)
      return {
        top: "var(--gap)",
        left: "var(--left-stitch)",
        width: "var(--ring-w)",
        height: barsCollapsed ? "var(--bar-h)" : "var(--half-h)",
      };
    const nextP = nextInRing(activeSection!);
    const bottomTop = barsCollapsed
      ? "calc(100dvh - var(--gap) - var(--bar-h))"
      : "calc(var(--gap) * 2 + var(--half-h))";
    const bottomH = barsCollapsed ? "var(--bar-h)" : "var(--half-h)";
    if (p === nextP)
      return {
        top: bottomTop,
        left: "var(--gap)",
        width: "var(--bottom-w)",
        height: bottomH,
      };
    return {
      top: bottomTop,
      left: "calc(var(--gap) * 2 + var(--bottom-w))",
      width: "var(--bottom-w)",
      height: bottomH,
    };
  }


  // Scrollbar thumb/track — цвет секции страницы
  useEffect(() => {
    const html = document.documentElement;
    const thumb =
      activeSection === "help"
        ? "var(--help)"
        : activeSection === "media"
          ? "var(--media)"
          : activeSection === "school"
            ? "var(--school)"
            : "rgba(1, 33, 104, 0.2)";
    const track =
      activeSection === "help"
        ? "rgba(6, 155, 147, 0.12)"
        : activeSection === "media"
          ? "rgba(255, 94, 14, 0.12)"
          : activeSection === "school"
            ? "rgba(17, 146, 230, 0.12)"
            : "transparent";
    html.style.setProperty("--scrollbar-thumb", thumb);
    html.style.setProperty("--scrollbar-track", track);
  }, [activeSection]);

  return (
    <div
      ref={ringRef}
      data-triad-ring=""
      style={
        {
          "--gap": "var(--gap-tile)",
          "--bar-h": "clamp(56px, 5dvh, 80px)",
          "--avail-w": "calc(100vw - var(--gap) * 2)",
          "--avail-h": "calc(100dvh - var(--gap) * 3)",
          "--half-h": "calc(var(--avail-h) / 2)",
          // --group-w defined in globals.css with @container override at ≤769px
          "--left-stitch": "calc(var(--gap) * 2 + var(--group-w))",
          "--ring-w": "calc(var(--avail-w) - var(--group-w) - var(--gap))",
          "--bottom-w": "calc((var(--avail-w) - var(--gap)) / 2)",
          "--tile-total-w": "calc(var(--avail-w) - var(--gap) * 2)",
          "--flex-0": flexes[0],
          "--flex-1": flexes[1],
          "--flex-2": flexes[2],
          "--total-flex": totalFlex,
          "--w-0":
            "calc(var(--tile-total-w) * var(--flex-0) / var(--total-flex))",
          "--w-1":
            "calc(var(--tile-total-w) * var(--flex-1) / var(--total-flex))",
          "--w-2":
            "calc(var(--tile-total-w) * var(--flex-2) / var(--total-flex))",
          "--left-0": "var(--gap)",
          "--left-1": "calc(var(--gap) * 2 + var(--w-0))",
          "--left-2": "calc(var(--gap) * 3 + var(--w-0) + var(--w-1))",
          fontFamily: "var(--font-content)",
          position: "relative",
          minHeight: "100dvh",
        } as React.CSSProperties
      }
    >
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 50,
          pointerEvents: "none",
        }}
      >
        <div
          ref={groupTileRef}
          onTransitionEnd={(e) => {
            // Bubble-guard: only react to this exact wrapper, not inner divs
            // (GroupSlider, logos, blur layers all have their own transitions).
            // 'width' is the canonical property — always changes home↔section.
            if (
              e.target === groupTileRef.current &&
              e.propertyName === "width"
            ) {
              setIsAnimating(false);
            }
          }}
          style={
            {
              position: "absolute",
              pointerEvents: "auto",
              top: groupRect.top,
              left: groupRect.left,
              width: groupRect.width,
              height: groupRect.height,
              "--tile-w": groupRect.width,
              "--tile-h": groupRect.height,
              // Container for GroupTile @container queries (border-radius, Mark/Full logo)
              containerType: "inline-size",
              transition: TILE_TR,
              zIndex: 2,
              willChange: isAnimating ? "top, left, width, height" : "auto",
            } as React.CSSProperties
          }
        >
          <GroupTile
            compact={barsCollapsed}
            hovered={hoveredTile === "group"}
            onHover={() => {
              if (!isAnimating) hoverEnter("group");
            }}
            onLeave={() => {
              if (!isAnimating) hoverLeave();
            }}
            homeLinkActive={isSection}
            radius={barsCollapsed ? 32 : 48}
            slideIndex={groupSlideIndex}
            onSlideChange={(i) => {
              setGroupSlideIndex(i);
              const sid = GROUP_SLIDES[i]?.sectionId as PanelId | null;
              if (isHome) setSliderPinnedSection(sid || null);
            }}
            slideColor={
              activeSection && !isHome ? META[activeSection].color : null
            }
            contentHidden={!isHome}
            isAnimating={isAnimating}
            isPageScrolled={isPageScrolled}
            slides={slides}
          />
        </div>
        <div
          style={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            clipPath: "none",
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          {PANELS.map((p) => {
            const rect = getSectionRect(p);
            const isActivePanel = p === activeSection && !isHome;
            let transform = "";
            let tileTransition: string = TILE_TR;
            if (switchInfo) {
              const { to, dir, phase } = switchInfo;
              const isEntering = p === to;
              if (phase === "init") {
                transform = isEntering
                  ? `translate3d(calc(${-dir} * var(--ring-w)), 0, 0)`
                  : `translate3d(calc(${dir} * (var(--bottom-w) + var(--gap))), 0, 0)`;
                tileTransition = "none";
              } else if (phase === "go") {
                transform = "translate3d(0,0,0)";
                tileTransition = `transform ${ANIM_DURATION_MS}ms cubic-bezier(0.25,0.1,0.25,1)`;
              }
            }
            return (
              <div
                key={p}
                style={
                  {
                    position: "absolute",
                    pointerEvents: "auto",
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height,
                    "--tile-w": rect.width,
                    "--tile-h": rect.height,
                    transform: transform || "translate3d(0,0,0)",
                    transition: tileTransition,
                    overflow: "visible",
                    willChange: isAnimating || switchInfo ? "transform, top, left, width, height" : "auto",
                  } as React.CSSProperties
                }
              >
                <SectionTile
                 panelId={p}
                 meta={META[p]}
                 compact={barsCollapsed}
                 isTouch={isTouch}
                 activeTouchId={activeTouchId}
                  hovered={
                    hoveredTile === `section-${p}` ||
                    isActivePanel ||
                    (sliderPinnedSection === p && isHome) ||
                    activeTouchId === p
                  }
                  siblingHovered={
                    isHome &&
                    ((hoveredTile !== null &&
                      hoveredTile !== `section-${p}` &&
                      hoveredTile.startsWith("section-")) ||
                      (sliderPinnedSection !== null &&
                        sliderPinnedSection !== p))
                  }
                  onHover={() => {
                    if (isAnimating) return;
                    hoverEnter(`section-${p}`);
                    // Слайдер и пин секции — только по клику "Развернуть",
                    // не при ховере
                  }}
                  onLeave={() => {
                    if (!isAnimating) hoverLeave();
                  }}
                  href={`/${p}`}
                  onLinkClick={onLinkClick(p)}
                  onPillClick={(e) => onPillClick(e, p)}
                  onExpand={() => {
                    if (isHome && SECTION_TO_SLIDE[p] !== undefined) {
                      setGroupSlideIndex(SECTION_TO_SLIDE[p]);
                      setPinnedSide('right');
                      setSliderPinnedSection(p);
                    }
                  }}
                  isExpanded={sliderPinnedSection === p && isHome && !isAnimating}
                  isCollapsedSibling={
                    groupIsNarrow &&
                    !barsCollapsed &&
                    isHome &&
                    !isAnimating &&
                    sliderPinnedSection !== null &&
                    sliderPinnedSection !== p
                  }
                  radius={barsCollapsed ? 32 : 48}
                  contentHidden={isActivePanel}
                  protectedMode={isProtectedSlide}
                  isAnimating={isAnimating}
                />
              </div>
            );
          })}
        </div>
      </div>
      <div
        style={{
          position: "relative",
          zIndex: 1,
          // display stays block for 450ms after isSection→false so opacity transition completes
            display: contentMounted ? "block" : "none",
            contain: "layout",
            pointerEvents: isSection && !isAnimating ? "auto" : "none",
            opacity: isSection && !isAnimating ? 1 : 0,
            transition: "opacity 0.4s ease",
            background: "transparent",
            willChange: "opacity",
          }}
        >
          {/* Only mount children when visible — prevents scrollIntoView firing on display:none elements */}
          {contentMounted ? children : null}
      </div>
    </div>
  );
}
